import axios from 'axios';
import { Identity as Identity$1, Parent as Parent$1, Assessment as Assessment$1, Student as Student$1, AuthHistory as AuthHistory$1, AuthStats as AuthStats$1, Avatar as Avatar$1, Buddy as Buddy$1, BuddyLeaderboard as BuddyLeaderboard$1, LessonResult as LessonResult$1, Lesson as Lesson$1, Challenge as Challenge$1, Feature as Feature$1, Feedback as Feedback$1, GlobalLeaderboard as GlobalLeaderboard$1, Locale, Item as Item$1, LessonFeedback as LessonFeedback$1, LessonLeaderboard as LessonLeaderboard$1, Message as Message$1, OrganizationContact as OrganizationContact$1, Preference as Preference$1, PaymentMethod, School as School$1, SchoolLeaderboard as SchoolLeaderboard$1, Sticker as Sticker$1, Subscription, StudentMathsAgeReport as StudentMathsAgeReport$1, StudentProgressionsReport as StudentProgressionsReport$1, StudentTopicsReport as StudentTopicsReport$1, StudentUsageReport as StudentUsageReport$1, SubscriptionPlan as SubscriptionPlan$1, SiblingDiscount, Teacher as Teacher$1, TopicProgress as TopicProgress$1, TopicReplay as TopicReplay$1, Topic, User as User$1, WeeklySummary as WeeklySummary$1, GroupReport as GroupReport$1, Group as Group$1, NewStudentBasic, TopicReplayEntry, NewTeacher as NewTeacher$1, LessonHistory as LessonHistory$1, TopicStudentsEntry as TopicStudentsEntry$1, StudentTopicFocus as StudentTopicFocus$1, GroupReportMetrics as GroupReportMetrics$1, GroupTopicProgress as GroupTopicProgress$1, GroupReportFiltersEnum, ConsistencyEnum, Model, StudentStatusEnum, ChallengeStats, CreditCard, Leaderboard, LeaderboardEntry, LessonLeaderboardEntry, ReportModel, StudentReportModel, TimeTaken, Usage, NewLessonOutcome, NewStudentTutorData, NewStudentTutorDataMetrics } from '@whizz/api-model';
import { DateTime } from 'luxon';

const DOMAIN = undefined !== window ? window.location.hostname.split('.').splice(-2).join('.') : undefined;
class Identity extends Identity$1 {
    static login(credentials) {
        // TODO: Check this token empty and this flow of creation (static <-> instance)
        return new this({ token: '' }).create(credentials);
    }
    static register(user, password, referralTrackingId = null, marketingReach = false) {
        if (!(user instanceof Parent$1)) {
            return Promise.reject(new Error('Invalid user type'));
        }
        const data = {
            password,
            marketing_reach: marketingReach,
            referral_tracking_id: referralTrackingId,
            parent: user,
        };
        return http()({
            method: 'POST',
            url: '/auth/register',
            data,
        })
            .then(({ data }) => {
            return user
                .setCountry(data.country)
                .setEmail(data.email)
                .setFirstName(data.first_name)
                .setId(data.id)
                .setLocale(data.locale)
                .setLastName(data.last_name)
                .setPartnerId(data.partner_id)
                .setPartnerManaged(data.partner_managed)
                .setPartnerName(data.partner_name)
                .setPartnerUrl(data.partner_url)
                .setPartnerUserId(data.partner_user_id)
                .setTimezone(data.timezone)
                .setSsoAccessId(data.sso_access_id)
                .setUsername(data.username);
        });
    }
    create(credentials) {
        return http()({
            method: 'POST',
            url: '/auth/login',
            data: credentials,
        })
            .then((response) => {
            return this.setToken(response.data.token);
        });
    }
    dashboard(params = {}) {
        return http()({
            method: 'GET',
            url: '/auth/dashboard',
            params,
        })
            .then(({ data }) => data);
    }
    destroy() {
        return http()({
            method: 'POST',
            url: '/auth/logout',
        })
            .catch((err) => (err.response && 401 === err.response.status) ? Promise.resolve(err.response) : Promise.reject(err))
            .then(() => {
            if ('undefined' !== typeof window) {
                window.document.cookie = `whizz_session=;domain=.${DOMAIN};path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            }
        });
    }
    goTo(destination, redirectWindow = null) {
        if (undefined === window) {
            return Promise.reject(new Error('Cannot redirect in non-browser environments'));
        }
        const redirectWindowInstance = redirectWindow || window;
        try {
            if (/(customer|my|service-combined|tutor)\.whizz\.(com|localhost)$/.test(new URL(destination).hostname)) {
                window.document.cookie = `whizz_session=${this.claims.authtoken};domain=.${DOMAIN};path=/`;
            }
        }
        catch (err) {
            // Ignore
        }
        redirectWindowInstance.location.href = destination;
        return Promise.resolve(destination);
    }
    goToDashboard(params = {}, redirectWindow = null) {
        return this.dashboard(params)
            .then((dashboard) => this.goTo(dashboard, redirectWindow));
    }
    goToReferrer(referrer, redirectWindow = null) {
        const url = new URL(referrer);
        if (window
            && (window.location.hostname !== url.hostname || window.location.pathname !== url.pathname) // Don't redirect back here
            && new RegExp(`(^|\\.)${DOMAIN}$`).test(url.hostname) // Only allow same-domain referrers
        ) {
            return this.goTo(referrer, redirectWindow);
        }
        return Promise.reject(new Error(`Invalid referrer: ${referrer}`));
    }
    updatePassword(current, next) {
        return http()({
            method: 'PUT',
            url: '/auth/password',
            data: {
                current,
                next,
            },
        })
            .then(() => Promise.resolve());
    }
}

var axiosReauth = (instance) => {
    if ('undefined' === typeof window) {
        return;
    }
    const loginHost = process.env.HOSTNAME_LOGIN || process.env.REACT_APP_HOSTNAME_LOGIN;
    const hostTest = new RegExp(`(${loginHost})(/(whizz-app))?`);
    instance.interceptors.response.use((value) => { return value; }, (err) => {
        if (err.response // A response was received
            && 401 === err.response.status // A 401 response was received
            && (Object.keys(err.config.headers).map((key) => key.toLowerCase()).includes('authorization')) // The request was authenticated
            && !hostTest.test(window.location.host) // Not already at the login page
        ) {
            const token = err.config.headers.Authorization ? err.config.headers.Authorization.replace(/^Bearer /, '') : err.config.headers.authorization.replace(/^Bearer /, '');
            const identity = new Identity({ token });
            window.location.assign(`https://${loginHost}?error=expired&username=${encodeURIComponent(identity.user.username)}&referer=${encodeURIComponent(window.location.href)}`);
        }
        return Promise.reject(err);
    });
};

let instance;
var http = (config) => {
    if (undefined === config && instance) {
        return instance;
    }
    instance = axios.create(Object.assign({
        baseURL: `https://${process.env.HOSTNAME_API || process.env.REACT_APP_HOSTNAME_API}`,
        headers: {
            'Content-Type': 'application/json',
        },
    }, config));
    axiosReauth(instance);
    return instance;
};

class Assessment extends Assessment$1 {
    static all(student) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/assessments`,
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static reset(student) {
        return http()({
            method: 'POST',
            url: `/students/${student instanceof Student$1 ? student.id : student}/reset`,
        })
            .then(() => Promise.resolve());
    }
}

class AuthHistory extends AuthHistory$1 {
    static all(limit = null) {
        return http()({
            method: 'GET',
            url: '/auth-histories',
            params: {
                limit,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
}

class AuthStats extends AuthStats$1 {
    static one() {
        return http()({
            method: 'GET',
            url: '/auth-stats',
        })
            .then(({ data }) => new this(data));
    }
}

class Avatar extends Avatar$1 {
    static one(student) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/avatar`,
        })
            .then(({ data }) => new this(data));
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/students/${this.student_id}/avatar`,
            data: this.updatable(),
        })
            .then(({ data }) => {
            return this
                .setAttributes(data.attributes)
                .setStudentId(data.student_id);
        });
    }
}

class Buddy extends Buddy$1 {
    static accepted(student) {
        return this.all(student, 'accepted');
    }
    static all(student, status = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/buddies`,
            params: {
                status,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static pending(student) {
        return this.all(student, 'pending');
    }
    accept() {
        return this
            .setStatus('accepted')
            .update();
    }
    create() {
        return http()({
            method: 'POST',
            url: `/students/${this.student_id}/buddies`,
            data: this.creatable(),
        })
            // TODO: `api` seems to return a 204 NO CONTENT. Maybe this should be `Promise.resolve()`
            .then((response) => {
            return this
                .setAvatar(response.data.avatar)
                .setBuddy(response.data.buddy)
                .setStatus(response.data.status)
                .setStudentId(response.data.student_id);
        });
    }
    destroy() {
        return http()({
            method: 'DELETE',
            url: `/students/${this.student_id}/buddies/${this.buddy.id}`,
        })
            .then(() => Promise.resolve());
    }
    reject() {
        return this
            .setStatus('rejected')
            .update();
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/students/${this.student_id}/buddies/${this.buddy.id}`,
            data: this.updatable(),
        })
            .then((response) => {
            return this
                .setAvatar(response.data.avatar)
                .setStatus(response.data.status)
                .setBuddy(response.data.buddy)
                .setStudentId(response.data.student_id);
        });
    }
}

class BuddyLeaderboard extends BuddyLeaderboard$1 {
    static one(student, limit = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/buddy-leaderboard`,
            params: {
                limit,
            },
        })
            .then(({ data }) => new this(data));
    }
}

class LessonResult extends LessonResult$1 {
    static all(student, start = null, end = null, modes = null, status = null, // TODO: Check this parameters as it is not included in `spec.openapi.yaml`
    lesson = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/lesson-results`,
            params: {
                status,
                end: end ? end.toUTC().toISO() : null,
                lesson: lesson ? (lesson instanceof Lesson$1 ? lesson.id : lesson) : null,
                modes: modes ? modes.join(',') : null,
                start: start ? start.toUTC().toISO() : null,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    create() {
        return http()({
            method: 'POST',
            url: `/students/${this.student_id}/lesson-results`,
            data: this.creatable(),
        })
            .then(({ data }) => {
            return this
                .setCompleted(data.completed)
                .setCompletedFlag(data.completed_flag)
                .setCredits(data.credits)
                .setHelp1(data.help1)
                .setHelp2(data.help2)
                .setHelp3(data.help3)
                .setId(data.id)
                .setIncompleteId(data.incomplete_id)
                .setIncompleteStarted(data.incomplete_started)
                .setLegacy(data.legacy)
                .setLesson(data.lesson)
                .setMode(data.mode)
                .setModeFlag(data.mode_flag)
                .setOutcome(data.outcome)
                .setProgression(data.progression)
                .setQuestionsAnswered(data.questions_answered)
                .setQuestionsTotal(data.questions_total)
                .setScore(data.score)
                .setStudentId(data.student_id)
                .setTimeTaken(data.time_taken)
                .setDetailedTimestamp(data.detailedTimestamp);
        });
    }
    update() {
        const update = this.updatable();
        return http()({
            method: 'PUT',
            url: `/students/${this.student_id}/lesson-results/${this.incomplete_id}`,
            data: update,
        })
            .then(({ data }) => {
            return this
                .setCompleted(data.completed)
                .setCompletedFlag(data.completed_flag)
                .setCredits(data.credits)
                .setHelp1(data.help1)
                .setHelp2(data.help2)
                .setHelp3(data.help3)
                .setId(data.id)
                .setIncompleteId(data.incomplete_id)
                .setIncompleteStarted(data.incomplete_started)
                .setLegacy(data.legacy)
                .setLesson(data.lesson)
                .setMode(data.mode)
                .setModeFlag(data.mode_flag)
                .setOutcome(data.outcome)
                .setProgression(data.progression)
                .setQuestionsAnswered(data.questions_answered)
                .setQuestionsTotal(data.questions_total)
                .setScore(data.score)
                .setStudentId(data.student_id)
                .setTimeTaken(data.time_taken)
                .setDetailedTimestamp(data.detailedTimestamp);
        });
    }
}

class Challenge extends Challenge$1 {
    static all(student, status = null, lesson = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/challenges`,
            params: {
                lesson: (lesson instanceof Lesson$1) ? lesson.id : null,
                status,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static completed(student) {
        return this.all(student, 'completed');
    }
    static pending(student) {
        return this.all(student, 'pending');
    }
    static search(student, lesson) {
        return this.all(student, null, lesson);
    }
    accept() {
        if ('pending' !== this.status) {
            throw new Error('This challenge cannot be accepted.');
        }
        return new LessonResult({
            mode: 'replay',
            legacy: {
                challengerID: this.challenger.id,
            },
            lesson: this.lesson,
            student_id: this.opponent.id,
        });
    }
    initiate() {
        if (this.status) {
            throw new Error('This challenge cannot be summoned.');
        }
        return new LessonResult({
            mode: 'replay',
            legacy: {
                challengedID: this.opponent.id,
            },
            lesson: this.lesson,
            student_id: this.challenger.id,
        });
    }
}

class Feature extends Feature$1 {
    static all(student) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/features`,
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(student, id) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/features/${id}`,
        })
            .then(({ data }) => new this(data));
    }
    create() {
        return http()({
            method: 'POST',
            url: `/students/${this.student_id}/features`,
            data: this.creatable(),
        })
            .then((response) => {
            return this
                .setId(response.data.id)
                .setMetadata(response.data.metadata)
                .setStudentId(response.data.student_id);
        });
    }
    destroy() {
        return http()({
            method: 'DELETE',
            url: `/students/${this.student_id}/features/${this.id}`,
        })
            .then(() => Promise.resolve());
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/students/${this.student_id}/features/${this.id}`,
            data: this.updatable(),
        })
            .then((response) => {
            return this
                .setId(response.data.id)
                .setMetadata(response.data.metadata)
                .setStudentId(response.data.student_id);
        });
    }
}

class Feedback extends Feedback$1 {
    create() {
        return http()({
            method: 'POST',
            url: `/students/${this.student_id}/feedbacks`,
            data: this.creatable(),
        })
            .then((response) => {
            return this
                .setCreated(response.data.created)
                .setMessage(response.data.message)
                .setStudentId(response.data.student_id);
        });
    }
}

class GlobalLeaderboard extends GlobalLeaderboard$1 {
    static one(locale = null, limit = null) {
        return http()({
            method: 'GET',
            url: '/global-leaderboard',
            params: {
                limit,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => new this(data));
    }
}

class Item extends Item$1 {
    static all(student) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/items`,
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(student, id /* uuid */) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/items/${id}`,
        })
            .then(({ data }) => new this(data));
    }
    create(quantityInput = '1') {
        const quantity = parseInt(quantityInput, 10);
        if (quantity < 1 || quantity > 10) {
            return Promise.reject(new Error(`Invalid quantity: ${quantity} (min: 1, max: 10)`));
        }
        const items = [];
        const attrs = this.toJSON();
        while (items.length < quantity) {
            const item = new Item(attrs);
            items.push(http()({
                method: 'POST',
                url: `/students/${this.student_id}/items`,
                data: item.creatable(),
            })
                .then((response) => item
                .setCatalogueMetadata(response.data.catalogue_metadata)
                .setDisplay(response.data.display)
                .setId(response.data.id)
                .setLastInteraction(response.data.last_interaction)
                .setPrice(response.data.price)
                .setPurchased(response.data.purchased)
                .setStudentId(response.data.student_id)));
        }
        return Promise.all(items)
            .then((items) => 1 === quantity ? items.shift() : items);
    }
    destroy() {
        return http()({
            method: 'DELETE',
            url: `/students/${this.student_id}/items/${this.id}`,
        })
            .then(() => Promise.resolve());
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/students/${this.student_id}/items/${this.id}`,
            data: this.updatable(),
        })
            .then((response) => this
            .setCatalogueMetadata(response.data.catalogue_metadata)
            .setDisplay(response.data.display)
            .setId(response.data.id)
            .setLastInteraction(response.data.last_interaction)
            .setPrice(response.data.price)
            .setPurchased(response.data.purchased)
            .setStudentId(response.data.student_id));
    }
}

class Lesson extends Lesson$1 {
    static all(locale = null) {
        return http()({
            method: 'GET',
            url: '/lessons',
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(student, locale = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/lessons/current`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => new this(response.data));
    }
    static skip(student) {
        return this.one(student)
            .then((lesson) => new LessonResult({
            lesson,
            mode: 'tutor',
            completed_flag: LessonResult.COMPLETED_FLAG_SKIPPED,
            student_id: student instanceof Student$1 ? student.id : student,
        }).create())
            .then(() => Promise.resolve());
    }
    hydrate() {
        return http()({
            method: 'GET',
            url: `/lessons/${this.id}`,
            params: {
                locale: this.locale.id,
            },
        })
            .then(({ data }) => {
            return this
                .setHasTest(data.has_test)
                .setId(data.id)
                .setLocale(data.locale)
                .setTargetTime(data.target_time)
                .setTitle(data.title)
                .setTopic(data.topic);
        });
    }
}

class LessonFeedback extends LessonFeedback$1 {
    create() {
        return http()({
            method: 'POST',
            url: `/students/${this.student_id}/lesson-feedbacks`,
            data: this.creatable(),
        })
            .then(({ data }) => {
            return this
                .setCreated(data.created)
                .setDescription(data.description)
                .setId(data.id)
                .setLesson(data.lesson)
                .setQuestionsCurrent(data.questions_current)
                .setQuestionsTotal(data.questions_total)
                .setStudentId(data.student_id)
                .setUserAgent(data.user_agent);
        });
    }
}

class LessonLeaderboard extends LessonLeaderboard$1 {
    static one(student, lesson, limit = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/lessons/${lesson instanceof Lesson$1 ? lesson.id : lesson}/leaderboard`,
            params: {
                limit,
            },
        })
            .then((response) => new this(response.data));
    }
}

class Message extends Message$1 {
    static all(student, type = null, locale = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/messages`,
            params: {
                type,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static allByGroup(group, type = null, locale = null) {
        return http()({
            method: 'GET',
            url: `/groups/${group}/messages`,
            params: {
                type,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static inbound(student, locale = null) {
        return this.all(student, 'inbound', locale);
    }
    static outbound(student, locale = null) {
        return this.all(student, 'outbound', locale);
    }
    create(locale = null) {
        if (this.to_multiple) {
            return this.createMultiple(locale);
        }
        return this.createSingle(locale);
    }
    createSingle(locale = null) {
        return http()({
            method: 'POST',
            url: `/students/${this.from instanceof Student$1 ? this.from.id : this.to.id}/messages`,
            data: this.creatable(),
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => {
            return this
                .setBody(data.body)
                .setFrom(data.from)
                .setFromDisplay(data.from_display)
                .setId(data.id)
                .setRead(data.read)
                .setReplyToMessageFromDisplay(data.reply_to_message_from_display)
                .setReplyToMessageId(data.reply_to_message_id)
                .setSent(data.sent)
                .setSticker(data.sticker)
                .setSubject(data.subject)
                .setTo(data.to);
        });
    }
    createMultiple(locale = null) {
        return http()({
            method: 'POST',
            url: '/teachers/messages',
            data: Object.assign(Object.assign({}, this.creatable()), { to_multiple: this.getToMultiple().map((student) => student.getId()) }),
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => {
            return this
                .setBody(data.body)
                .setFrom(data.from)
                .setFromDisplay(data.from_display)
                .setSent(data.sent)
                .setSticker(data.sticker)
                .setSubject(data.subject)
                .setToMultiple(data.to_multiple);
        });
    }
    destroy() {
        return http()({
            method: 'DELETE',
            url: `/students/${this.from instanceof Student$1 ? this.from.id : this.to.id}/messages/${this.id}`,
        })
            .then(() => Promise.resolve());
    }
    markAsRead(locale = null) {
        return this.setRead(true).update(locale);
    }
    markAsUnread(locale = null) {
        return this.setRead(false).update(locale);
    }
    update(locale = null) {
        return http()({
            method: 'PUT',
            url: `/students/${this.from instanceof Student$1 ? this.from.id : this.to.id}/messages/${this.id}`,
            data: this.updatable(),
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => {
            return this
                .setBody(data.body)
                .setFrom(data.from)
                .setFromDisplay(data.from_display)
                .setId(data.id)
                .setRead(data.read)
                .setReplyToMessageFromDisplay(data.reply_to_message_from_display)
                .setReplyToMessageId(data.reply_to_message_id)
                .setSent(data.sent)
                .setSticker(data.sticker)
                .setSubject(data.subject)
                .setTo(data.to);
        });
    }
}

class OrganizationContact extends OrganizationContact$1 {
    static one(id) {
        return http()({
            method: 'GET',
            url: `/organization-contacts/${id}`,
        })
            .then(({ data }) => new this(data));
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class Parent extends Parent$1 {
    static one(id) {
        return http()({
            method: 'GET',
            url: `/parents/${id}`,
        })
            .then((response) => new this(response.data));
    }
    static preferences(id) {
        return http()({
            method: 'GET',
            url: `/parents/${id}/preferences`,
        }).then((response) => new Preference$1(response.data));
    }
    getPaymentMethod() {
        return http()({
            method: 'GET',
            url: `/parents/${this.id}/payment-method`,
        })
            .then((response) => {
            const _a = response.data, { type } = _a, attrs = __rest(_a, ["type"]);
            return PaymentMethod.createFromType(type, attrs);
        });
    }
    getSubscribedProducts() {
        return http()({
            method: 'GET',
            url: `/parents/${this.id}/subscribed-products`,
        })
            .then((response) => response.data.map((data) => data));
    }
    removePaymentMethod() {
        return http()({
            method: 'DELETE',
            url: `/parents/${this.id}/payment-method`,
        });
    }
    setPaymentMethod(paymentMethodId = null) {
        if (!paymentMethodId) {
            return http()({
                method: 'POST',
                url: `/parents/${this.id}/payment-method`,
            }).then(({ data }) => data);
        }
        return http()({
            method: 'POST',
            url: `/parents/${this.id}/payment-method`,
            data: {
                stripe_payment_method_id: paymentMethodId,
            },
        }).then(({ data }) => {
            const { type } = data, attrs = __rest(data, ["type"]);
            return PaymentMethod.createFromType(type, attrs);
        });
    }
    updatePreferences(preference) {
        return http()({
            method: 'PUT',
            url: `/parents/${this.id}/preferences/notification`,
            data: preference,
        });
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/parents/${this.id}`,
            data: this.updatable(),
        })
            .then(({ data }) => {
            return this
                .setCountry(data.country)
                .setEmail(data.email)
                .setFirstName(data.first_name)
                .setId(data.id)
                .setLastName(data.last_name)
                .setPartnerId(data.partner_id)
                .setPartnerManaged(data.partner_managed)
                .setPartnerName(data.partner_name)
                .setPartnerUrl(data.partner_url)
                .setPartnerUserId(data.partner_user_id)
                .setTimezone(data.timezone)
                .setSsoAccessId(data.sso_access_id)
                .setUsername(data.username);
        });
    }
}

class Preference extends Preference$1 {
}

class School extends School$1 {
    static academicYearStartDate(schoolId) {
        return http()({
            method: 'GET',
            url: `/schools/${schoolId}/academic-year-start/`,
        })
            .then(({ data }) => DateTime.fromISO(data));
    }
    static getSubscribedProducts(schoolId) {
        return http()({
            method: 'GET',
            url: `/schools/${schoolId}/subscribed-products`,
        })
            .then((response) => response.data.map((data) => data));
    }
    static get(schoolId) {
        return http()({
            method: 'GET',
            url: `/schools/${schoolId}/info`,
        })
            .then(({ data }) => data);
    }
}

class SchoolLeaderboard extends SchoolLeaderboard$1 {
    static one(school, limit = null) {
        return http()({
            method: 'GET',
            url: `/schools/${school instanceof School$1 ? school.id : school}/leaderboard`,
            params: {
                limit,
            },
        })
            .then(({ data }) => new this(data));
    }
}

class Sticker extends Sticker$1 {
    static all(locale = null) {
        return http()({
            method: 'GET',
            url: '/stickers',
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(id, locale = null) {
        return http()({
            method: 'GET',
            url: `/stickers/${id}`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => new this(data));
    }
}

class Student extends Student$1 {
    static all() {
        return http()({
            method: 'GET',
            url: '/students',
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(id) {
        return http()({
            method: 'GET',
            url: `/students/${id}`,
        })
            .then(({ data }) => new this(data));
    }
    static passwordReset() {
        return Promise.reject(new Error('Method not allowed'));
    }
    static passwordResetConfirm() {
        return Promise.reject(new Error('Method not allowed'));
    }
    create(password) {
        return http()({
            method: 'POST',
            url: '/students',
            data: {
                password,
                student: this.creatable(),
            },
        })
            .then(({ data }) => {
            return this
                .setActive(data.active)
                .setCredits(data.credits)
                .setDob(data.dob)
                .setGender(data.gender)
                .setLastAssessment(data.last_assessment)
                .setLocaleAlternatives(data.locale_alternatives)
                .setLocalePreferred(data.locale_preferred)
                .setOwner(data.owner)
                .setParentId(data.parent_id)
                .setParentLinkable(data.parent_linkable)
                .setSchoolId(data.school_id)
                .setTopicFocus(data.topic_focus)
                .setUsageCap(data.usage_cap)
                .setCountry(data.country)
                .setFirstName(data.first_name)
                .setId(data.id)
                .setLastName(data.last_name)
                .setPartnerId(data.partner_id)
                .setPartnerManaged(data.partner_managed)
                .setPartnerName(data.partner_name)
                .setPartnerUrl(data.partner_url)
                .setPartnerUserId(data.partner_user_id)
                .setTimezone(data.timezone)
                .setSsoAccessId(data.sso_access_id)
                .setUsername(data.username);
        });
    }
    createSubscription(subscription, coupon = null) {
        return http()({
            method: 'POST',
            url: `/students/${this.id}/subscription`,
            data: {
                coupon,
                subscription: subscription.creatable(),
            },
        })
            .then(({ data }) => {
            if (data) {
                return subscription
                    .setCanceled(data.canceled)
                    .setEnd(data.end)
                    .setGrace(data.grace)
                    .setGraceRequiresAction(data.grace_requires_action)
                    .setId(data.id)
                    .setLocale(data.locale)
                    .setNextPlan(data.next_plan)
                    .setPlan(data.plan)
                    .setStart(data.start)
                    .setStudentId(data.student_id)
                    .setTrial(data.trial);
            }
            else {
                return null;
            }
        });
    }
    getSubscription() {
        return http()({
            method: 'GET',
            url: `/students/${this.id}/subscription`,
        })
            .then(({ data }) => data ? new Subscription(data) : null);
    }
    getStudentPassword() {
        return http()({
            method: 'GET',
            url: `/students/${this.id}/password`,
        })
            .then(({ data }) => data);
    }
    getStudentPin() {
        return http()({
            method: 'GET',
            url: `/students/${this.id}/pin`,
        })
            .then(({ data }) => data);
    }
    pay(amount) {
        return this.setCredits(this.credits - parseInt(amount, 10)).update();
    }
    update() {
        return http()({
            method: 'PUT',
            url: `/students/${this.id}`,
            data: this.updatable(),
        })
            .then(({ data }) => {
            return this
                .setActive(data.active)
                .setCredits(data.credits)
                .setDob(data.dob)
                .setGender(data.gender)
                .setLastAssessment(data.last_assessment)
                .setLocaleAlternatives(data.locale_alternatives)
                .setLocalePreferred(data.locale_preferred)
                .setOwner(data.owner)
                .setParentId(data.parent_id)
                .setParentLinkable(data.parent_linkable)
                .setSchoolId(data.school_id)
                .setTopicFocus(data.topic_focus)
                .setUsageCap(data.usage_cap)
                .setCountry(data.country)
                .setFirstName(data.first_name)
                .setId(data.id)
                .setLastName(data.last_name)
                .setPartnerId(data.partner_id)
                .setPartnerManaged(data.partner_managed)
                .setPartnerName(data.partner_name)
                .setPartnerUrl(data.partner_url)
                .setPartnerUserId(data.partner_user_id)
                .setTimezone(data.timezone)
                .setSsoAccessId(data.sso_access_id)
                .setUsername(data.username);
        });
    }
    updatePassword(password) {
        return http()({
            method: 'PUT',
            url: `/students/${this.id}/password`,
            data: { password },
        })
            .then(() => Promise.resolve());
    }
    updateSubscription(subscription) {
        return http()({
            method: 'PUT',
            url: `/students/${this.id}/subscription`,
            data: subscription.updatable(),
        })
            .then(({ data }) => {
            if (data) {
                return subscription
                    .setCanceled(data.canceled)
                    .setEnd(data.end)
                    .setGrace(data.grace)
                    .setGraceRequiresAction(data.grace_requires_action)
                    .setId(data.id)
                    .setLocale(data.locale)
                    .setNextPlan(data.next_plan)
                    .setPlan(data.plan)
                    .setStart(data.start)
                    .setStudentId(data.student_id)
                    .setTrial(data.trial);
            }
            else {
                return null;
            }
        });
    }
    static bonus(id) {
        return http()({
            method: 'GET',
            url: `/students/${id}/bonus`,
        })
            .then(({ data }) => data);
    }
}

class StudentMathsAgeReport extends StudentMathsAgeReport$1 {
    static one(student, start, end = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/maths-age-report`,
            params: {
                end: end ? end.toUTC().toISO() : null,
                start: start.toUTC().toISO(),
            },
        })
            .then((response) => new this(response.data));
    }
}

class StudentProgressionsReport extends StudentProgressionsReport$1 {
    static one(student, start, end = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/progressions-report`,
            params: {
                end: end ? end.toUTC().toISO() : null,
                start: start.toUTC().toISO(),
            },
        })
            .then((response) => new this(response.data));
    }
}

class StudentTopicsReport extends StudentTopicsReport$1 {
    static one(student, start, end = null, locale = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/topics-report`,
            params: {
                end: end ? end.toUTC().toISO() : null,
                start: start.toUTC().toISO(),
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => new this(response.data));
    }
}

class StudentUsageReport extends StudentUsageReport$1 {
    static one(student, start, end = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/usage-report`,
            params: {
                end: end ? end.toUTC().toISO() : null,
                start: start.toUTC().toISO(),
            },
        })
            .then((response) => new this(response.data));
    }
}

class SubscriptionPlan extends SubscriptionPlan$1 {
    static all(locale, coupon = null) {
        return http()({
            method: 'GET',
            url: '/subscription-plans',
            params: {
                coupon,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
    static one(id, coupon = null) {
        return http()({
            method: 'GET',
            url: `/subscription-plans/${id}`,
            params: {
                coupon,
            },
        })
            .then(({ data }) => new this(data));
    }
    static siblingDiscount(id) {
        return http()({
            method: 'GET',
            url: `/subscription-plans/${id}/sibling-discount`,
        })
            .then(({ data }) => new SiblingDiscount(data));
    }
}

class Teacher extends Teacher$1 {
    static one(id) {
        return http()({
            method: 'GET',
            url: `/teachers/${id}`,
        })
            .then(({ data }) => new this(data.chukModel));
    }
}

class TopicProgress extends TopicProgress$1 {
    static all(student, locale = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/topic-progresses`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
}

class TopicReplay extends TopicReplay$1 {
    static one(student, topic, locale = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/topic-replays/${topic instanceof Topic ? topic.id : topic}`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => new this(response.data));
    }
}

class User extends User$1 {
    static passwordReset(username) {
        return http()({
            method: 'POST',
            url: '/auth/password-reset',
            data: { username },
        })
            .then(() => { });
    }
    static passwordResetConfirm(username, token, password) {
        return http()({
            method: 'PUT',
            url: '/auth/password-reset',
            data: {
                password,
                token,
                username,
            },
        })
            .then(() => { });
    }
}

class WeeklySummary extends WeeklySummary$1 {
    static all(student, limit = null) {
        return http()({
            method: 'GET',
            url: `/students/${student instanceof Student$1 ? student.id : student}/weekly-summaries`,
            params: {
                limit,
            },
        })
            .then((response) => response.data.map((data) => new this(data)));
    }
}

class GroupReport extends GroupReport$1 {
    // TODO: group should be a model (maybe NewClass or NewGroup)
    static oneByGroupId(group, params = {}) {
        const formattedParams = { start_date: DateTime.now().minus({ weeks: 8 }).toUTC().toISO() };
        if (null != params.startDate) {
            if (DateTime.isDateTime(params.startDate)) {
                formattedParams.start_date = params.startDate.toUTC().toISO();
            }
            else {
                formattedParams.start_date = params.startDate;
            }
        }
        if (null != params.endDate) {
            formattedParams.end_date = params.endDate.toUTC().toISO();
        }
        if (null != params.recentDate) {
            formattedParams.recent_date = params.recentDate.toUTC().toISO();
        }
        return http()({
            method: 'GET',
            url: `/groups/${group}/report`,
            params: formattedParams,
        })
            .then(({ data }) => new this(data));
    }
}

class Group extends Group$1 {
    static getStudentsById(groupId) {
        return http()({
            method: 'GET',
            url: `/groups/${groupId}/students`,
        }).then(({ data }) => data.map((st) => new NewStudentBasic(st)));
    }
    static getTopicReplayReportById(groupId) {
        return http()({
            method: 'GET',
            url: `/groups/${groupId}/students/topic-replay`,
        }).then(({ data }) => data.map((st) => new TopicReplayEntry(st)));
    }
}

class NewTeacher extends NewTeacher$1 {
    static getClasses(teacherId) {
        return http()({
            method: 'GET',
            url: `/teachers/${teacherId}/classes`,
        })
            .then((response) => response.data);
    }
    static one(id) {
        return http()({
            method: 'GET',
            url: `/teachers/${id}`,
        })
            .then(({ data }) => new this(data));
    }
}

class LessonHistory extends LessonHistory$1 {
    static manyByStudentId(studentId, groupId, locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (endDate)
            ? endDate.toISO()
            : DateTime.now().toISO();
        return http()({
            method: 'GET',
            url: `/students/${studentId}/lesson-history/`,
            params: {
                group_id: groupId,
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data.map((lesson) => new this(lesson)));
    }
}

class TopicStudentsEntry extends TopicStudentsEntry$1 {
    static byGroupId(groupId, locale = null) {
        return http()({
            method: 'GET',
            url: `/groups/${groupId}/topics-students`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then((response) => response.data.map((data) => new TopicStudentsEntry(data)));
    }
}

class StudentTopicFocus extends StudentTopicFocus$1 {
    static createMultiple(students) {
        const data = {};
        for (const student of students) {
            data[student.studentId.toString()] = student;
        }
        return http()({
            method: 'POST',
            url: '/students/topics-focus',
            data: data,
        })
            .then(() => {
            return;
        });
    }
    static removeMultiple(students) {
        const data = {};
        for (const student of students) {
            data[student.studentId.toString()] = student;
        }
        return http()({
            method: 'DELETE',
            url: '/students/topics-focus',
            data: data,
        })
            .then(() => {
            return;
        });
    }
}

class GroupReportMetrics extends GroupReportMetrics$1 {
    static oneByGroupId(group, locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (endDate)
            ? endDate.toISO()
            : DateTime.now().toISO();
        return http()({
            method: 'GET',
            url: `/groups/${group}/report-metrics`,
            params: {
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => new this(data));
    }
}

class GroupTopicProgress extends GroupTopicProgress$1 {
    static byGroup(group, params = {}) {
        const formattedParams = { start_date: DateTime.now().minus({ weeks: 8 }).toUTC().toISO() };
        if (null != params.startDate) {
            if (DateTime.isDateTime(params.startDate)) {
                formattedParams.start_date = params.startDate.toUTC().toISO();
            }
            else {
                formattedParams.start_date = params.startDate;
            }
        }
        if (null != params.endDate) {
            formattedParams.end_date = params.endDate.toUTC().toISO();
        }
        return http()({
            method: 'GET',
            url: `/groups/${group}/topic-progress`,
            params: formattedParams,
        })
            .then(({ data }) => new GroupTopicProgress(data));
    }
}

class HallOfFame {
    static get() {
        return http()({
            method: 'GET',
            url: '/hall-of-fame',
        })
            .then((response) => response.data);
    }
}

class Guardians {
    static get(studentToken) {
        return http()({
            method: 'GET',
            url: '/students/guardians/',
            headers: { Authorization: `Bearer ${studentToken}` },
        })
            .then((response) => response.data);
    }
    static put(studentToken, guardians) {
        return http()({
            method: 'PUT',
            url: '/students/guardians/',
            headers: { Authorization: `Bearer ${studentToken}` },
            data: { guardians },
        })
            .then((response) => response.data);
    }
    static putByIdFromSchool(childId, guardians) {
        return http()({
            method: 'PUT',
            url: `/students/${childId}/guardians/`,
            data: { guardians },
        })
            .then((response) => response.data);
    }
    static acceptConsent(consentHash) {
        return http()({
            method: 'POST',
            url: '/students/guardians/consent',
            params: { consentHash },
        })
            .then(() => { });
    }
    static unsubscribe(unsubscribeHash) {
        return http()({
            method: 'POST',
            url: '/students/guardians/unsubscribe',
            params: { unsubscribeHash },
        })
            .then(() => { });
    }
}

class CsvReports {
    static groupById(group, locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (typeof endDate === 'string')
            ? endDate
            : endDate.toISO();
        return http()({
            method: 'GET',
            url: `/groups/${group}/report-metrics-csv`,
            params: {
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data);
    }
    static schoolAllStudents(group, locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (typeof endDate === 'string')
            ? endDate
            : endDate.toISO();
        return http()({
            method: 'GET',
            url: '/schools/report-csv/students',
            params: {
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data);
    }
    static schoolClasses(group, locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (typeof endDate === 'string')
            ? endDate
            : endDate.toISO();
        return http()({
            method: 'GET',
            url: '/schools/report-csv/classes',
            params: {
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data);
    }
}

class StudentLoginSheets {
    static get(group) {
        return http()({
            method: 'GET',
            url: `/groups/${group}/student-login-sheets`,
        })
            .then((response) => response.data);
    }
}

class GroupPictureLogin {
    static get(accessCode) {
        return http()({
            method: 'GET',
            url: `/groups/${accessCode}/picture-login`,
        })
            .then((response) => response.data);
    }
}

class GroupTopicReportRepository {
    static byGroup(group, params = {}) {
        const formattedParams = { start_date: DateTime.now().minus({ weeks: 8 }).toUTC().toISO() };
        if (null != params.startDate) {
            if (DateTime.isDateTime(params.startDate)) {
                formattedParams.start_date = params.startDate.toUTC().toISO();
            }
            else {
                formattedParams.start_date = params.startDate;
            }
        }
        if (null != params.endDate) {
            formattedParams.end_date = params.endDate.toUTC().toISO();
        }
        return http()({
            method: 'GET',
            url: `/groups/${group}/topic-report`,
            params: formattedParams,
        })
            .then(({ data }) => data);
    }
}

class GroupReportMetricsRepository {
    static callApiForOne(group, filter, startDate, endDate, locale = null) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (endDate)
            ? endDate.toISO()
            : DateTime.now().toISO();
        return http()({
            method: 'GET',
            url: `/groups/${group}/report-metrics`,
            params: {
                filter,
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data);
    }
    static oneWithAllStudentsOfGroup(group, locale = null, startDate, endDate) {
        return this.callApiForOne(group, null, startDate, endDate, locale);
    }
    static oneWithAssessedNotBeingReassessedStudents(group, locale = null, startDate, endDate) {
        const filter = GroupReportFiltersEnum.ASSESSED_NOT_BEING_REASSESSED;
        return this.callApiForOne(group, filter, startDate, endDate, locale);
    }
    static oneWithUnassessedOrReassessedStudent(group, locale = null, startDate, endDate) {
        const filter = GroupReportFiltersEnum.NOT_ASSESSED_OR_REASSESSED;
        return this.callApiForOne(group, filter, startDate, endDate, locale);
    }
    static oneWithHaveNeverBeenAssessedStudents(group, locale = null, startDate, endDate) {
        const filter = GroupReportFiltersEnum.HAVE_NEVER_BEEN_ASSESSED;
        return this.callApiForOne(group, filter, startDate, endDate, locale);
    }
    static oneWithAssessedOrBeingReassessedStudents(group, locale = null, startDate, endDate) {
        const filter = GroupReportFiltersEnum.ASSESSED_OR_BEING_REASSESSED;
        return this.callApiForOne(group, filter, startDate, endDate, locale);
    }
    static getConsistencyName(consistency, totalWeeks) {
        const weeksConsistencyPercentage = consistency / totalWeeks * 100;
        if (87.5 < weeksConsistencyPercentage) {
            return ConsistencyEnum.CONSTANT;
        }
        if (87.5 >= weeksConsistencyPercentage && 62.5 < weeksConsistencyPercentage) {
            return ConsistencyEnum.RELIABLE;
        }
        if (62.5 >= weeksConsistencyPercentage && 37.5 < weeksConsistencyPercentage) {
            return ConsistencyEnum.STEADY;
        }
        if (37.5 >= weeksConsistencyPercentage && 12.5 < weeksConsistencyPercentage) {
            return ConsistencyEnum.INTERMITTENT;
        }
        return ConsistencyEnum.OCCASIONAL;
    }
}

class SchoolReportRepository {
    static one(locale = null, startDate, endDate) {
        const start_date = (typeof startDate === 'string')
            ? startDate
            : startDate.toISO();
        const end_date = (endDate)
            ? endDate.toISO()
            : DateTime.now().toISO();
        return http()({
            method: 'GET',
            url: '/schools/report',
            params: {
                start_date,
                end_date,
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(({ data }) => data);
    }
}

class GroupTopicFocusRepository {
    static byGroupId(groupId, locale = null) {
        return http()({
            method: 'GET',
            url: `/groups/${groupId}/topic-focus`,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        }).then(({ data }) => data);
    }
}

class NewTeacherRepository extends NewTeacher$1 {
    static getClasses(teacherId) {
        return http()({
            method: 'GET',
            url: `/teachers/${teacherId}/classes`,
        })
            .then((response) => response.data);
    }
    static updateAdminRights({ teacherId, newAdminValue }) {
        return http()({
            method: 'PUT',
            url: `/teachers/${teacherId}/isAdmin`,
            data: { newAdminValue },
        })
            .then((response) => response.data);
    }
    static updateSubscriptionWc({ teacherId, wantsToSubscribe }) {
        return http()({
            method: 'PUT',
            url: `/teachers/${teacherId}/wcu-subscription`,
            data: { wantsToSubscribe },
        })
            .then((response) => response.data);
    }
    static getLoggedTeacher() {
        return http()({
            method: 'GET',
            url: '/teacher',
        })
            .then((response) => response.data);
    }
    static isPasswordUpdateNeeded() {
        return http()({
            method: 'GET',
            url: '/teacher/password-update',
        })
            .then((response) => response.data);
    }
}

class GroupReportRepository {
    static oneByGroupId(group, params = {}) {
        const formattedParams = { start_date: DateTime.now().minus({ weeks: 8 }).toUTC().toISO() };
        if (null != params.startDate) {
            if (DateTime.isDateTime(params.startDate)) {
                formattedParams.start_date = params.startDate.toUTC().toISO();
            }
            else {
                formattedParams.start_date = params.startDate;
            }
        }
        if (null != params.endDate) {
            formattedParams.end_date = params.endDate.toUTC().toISO();
        }
        if (null != params.mock) {
            formattedParams.mock = params.mock.toString();
        }
        if (null != params.recentDate) {
            formattedParams.recent_date = params.recentDate.toUTC().toISO();
        }
        return http()({
            method: 'GET',
            url: `/groups/${group}/report`,
            params: formattedParams,
        })
            .then(({ data }) => ({
            startDate: DateTime.fromISO(data.startDate),
            endDate: DateTime.fromISO(data.endDate),
            recentDate: DateTime.fromISO(data.recentDate),
            hallOfFame: data.hallOfFame,
            students: data.students.map((student) => (Object.assign(Object.assign({}, student), { lastLogonDate: null != student.lastLogonDate ? DateTime.fromISO(student.lastLogonDate) : null }))),
        }));
    }
    static getStudentsGoingWell(groupReport) {
        return groupReport.students.filter((student) => {
            const { haveCompletedAssessment, lastLogonDate, progressions } = student;
            return progressions > 0 && null !== lastLogonDate && haveCompletedAssessment;
        });
    }
    static getStudentsWithNoLogin(groupReport) {
        // Never logged in: `lastLogonDate` being null
        return groupReport.students.filter((student) => null === student.lastLogonDate);
    }
    static getStudentsWithNoAssessment(groupReport) {
        // haven't completed means `lastLogonDate` != null and their mathsAge is 0
        return groupReport.students.filter((student) => {
            const { mathsAge, lastLogonDate } = student;
            return null !== lastLogonDate && mathsAge === 0;
        });
    }
    static getStudentsWithNoProgression(groupReport) {
        // haven't made progressions: progressions == 0 and `lastLogonDate` != null and
        // `Pupil`.`assessmentCompleted` == null && and all `assessments`.`completed` records are not null
        return groupReport.students.filter((student) => {
            const { progressions, lastLogonDate, haveCompletedAssessment } = student;
            return progressions === 0 && null !== lastLogonDate && haveCompletedAssessment;
        });
    }
    static getStudentsInReassessment(groupReport) {
        return groupReport.students.filter((student) => {
            const { isBeingReassessed, mathsAge } = student;
            return isBeingReassessed && mathsAge > 0;
        });
    }
}

class NewStudentTutorDataRepository {
    static getStuckLesson(newStudentTutorData) {
        const eightWeeksAgo = DateTime.now().minus({ weeks: 8 }).toMillis();
        const reassessmentStartDate = newStudentTutorData.hasBeenReassessed && newStudentTutorData.reassessmentStartDate
            ? (DateTime.isDateTime(newStudentTutorData.reassessmentStartDate) ? newStudentTutorData.reassessmentStartDate : DateTime.fromISO(newStudentTutorData.reassessmentStartDate)).toMillis()
            : null;
        const completedLessons = NewStudentTutorDataRepository.getOrderedLastLessonOutcomesByLessonIDAndTimeStamp(newStudentTutorData)
            .filter((lesson) => {
            const isNotCompleted = !lesson.completed;
            const hasNoOutcome = !lesson.outcome;
            const isNotFailedOrStatic = lesson.outcome ? !['failed', 'static'].includes(lesson.outcome) : true;
            if (isNotCompleted || hasNoOutcome || !lesson.timeStamp || isNotFailedOrStatic) {
                return false;
            }
            const timeStamp = Model.castToDate(lesson.timeStamp);
            if (!timeStamp) {
                return false;
            }
            const timeStampAsNumber = timeStamp.toMillis();
            return (newStudentTutorData.hasBeenReassessed ?
                timeStampAsNumber > reassessmentStartDate :
                timeStampAsNumber > eightWeeksAgo // TODO: Nic thinks we can improve this line going to its first assessment date
            );
        });
        const error = new Error('No stuck lesson were found in the given object');
        if (completedLessons.length === 0) {
            throw error;
        }
        let count = 1;
        let lastSortedLesson = completedLessons.shift();
        for (const sortedLesson of completedLessons) {
            if (lastSortedLesson.lessonID !== sortedLesson.lessonID) {
                count = 1;
                lastSortedLesson = sortedLesson;
                continue;
            }
            if (++count > 1 && NewStudentTutorDataRepository.hasNeverPassedLesson(newStudentTutorData, sortedLesson)) {
                return sortedLesson;
            }
        }
        throw new Error('No stuck lesson were found in the given object');
    }
    static getOrderedLastLessonOutcomesByLessonIDAndTimeStamp(newStudentTutorData) {
        return newStudentTutorData.lastLessonOutcomes
            .sort((a, b) => {
            if (a.lessonID === b.lessonID) {
                if (a.timeStamp === b.timeStamp) {
                    return 0;
                }
                // Returns `a` before `b` if `a` is nearer in time
                return a.timeStamp > b.timeStamp ? -1 : 1;
            }
            return a.lessonID > b.lessonID ? -1 : 1;
        });
    }
    static hasNeverPassedLesson(newStudentTutorData, sortedLesson) {
        return 0 === newStudentTutorData.lastLessonOutcomes
            .filter((lesson) => lesson.lessonID === sortedLesson.lessonID && lesson.completed && lesson.outcome === 'passed')
            .length;
    }
    static getProgressingSlowlyLessons(newStudentTutorData) {
        // Supposing `lastLessonOutcomes` came ordered by `timestamp` in DESC order
        const lastLessonOutcomesPassed = newStudentTutorData.lastLessonOutcomes
            .filter((lesson) => !lesson.isJumpingMode && lesson.stackDepth === 1);
        if (lastLessonOutcomesPassed.length < 5) {
            return [];
        }
        const lastFiveLessonOutcomes = lastLessonOutcomesPassed.slice(0, 5);
        const totalTimeTakenSeconds = lastFiveLessonOutcomes
            .map((lessonOutcome) => lessonOutcome.timeTaken)
            .reduce((accumulator, current) => accumulator + current, 0); // Sum time taken
        const totalExpectedTimeTakenSeconds = lastFiveLessonOutcomes
            .map((lessonOutcome) => lessonOutcome.expectedTimeTaken)
            .reduce((accumulator, current) => accumulator + current, 0); // Sum expected time taken
        if (totalTimeTakenSeconds > 1.5 * totalExpectedTimeTakenSeconds) {
            return lastFiveLessonOutcomes;
        }
        return [];
    }
    static isStuck(newStudentTutorData) {
        // Filter by lesson completed with certain outcome. If failed in the last 8 weeks or from reassessment date, then look at the full history
        try {
            NewStudentTutorDataRepository.getStuckLesson(newStudentTutorData);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    static isStruggling(newStudentTutorData) {
        const a = newStudentTutorData.lastLessonOutcomes
            .filter((lesson) => (newStudentTutorData.hasBeenReassessed
            ? lesson.timeStamp > newStudentTutorData.reassessmentEndDate
            : true))
            .slice(0, 10)
            .filter((lesson) => lesson.completed && 'passed' !== lesson.outcome);
        return a.length >= 4;
    }
}

class PdfRepository {
    // Returned base64 string
    static buildClassReportPdf(studentsIds, classId, dateInterval, startDate, endDate, mathsAge, classAverage, classChart) {
        return http()({
            method: 'GET',
            url: '/pdf/group-report',
            params: {
                ids: studentsIds,
                class_id: classId,
                date_interval: dateInterval,
                start_date: startDate,
                end_date: endDate,
                maths_age: mathsAge,
                class_average: classAverage,
                class_chart: classChart,
            },
        })
            .then(({ data }) => data);
    }
}

class StudentRepository {
    static getStudentStatus(progressions) {
        if (0 == progressions) {
            return StudentStatusEnum.ROOKIE;
        }
        if (4 > progressions) {
            return StudentStatusEnum.APPRENTICE;
        }
        if (6 > progressions) {
            return StudentStatusEnum.PRO;
        }
        if (8 > progressions) {
            return StudentStatusEnum.MASTER;
        }
        return StudentStatusEnum.SUPERSTAR;
    }
    static create(student) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: '/students/create',
                data: student,
            })
                .then(({ data }) => data);
        });
    }
    static update(childId, student) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'PUT',
                url: `/students/${childId}/update`,
                data: student,
            })
                .then(() => { });
        });
    }
    static rosterManagementListItemsByAction(action, extraData) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'GET',
                url: '/students',
                params: Object.assign({ action: action }, (extraData !== null && extraData !== void 0 ? extraData : {})),
            })
                .then(({ data }) => data);
        });
    }
    static activate(childrenIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/activate`,
                data: {
                    childrenIds: childrenIds,
                },
            })
                .then(() => { });
        });
    }
    static deactivate(childrenIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/deactivate`,
                data: {
                    childrenIds: childrenIds,
                },
            })
                .then(() => { });
        });
    }
    static archive(childrenIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/archive`,
                data: {
                    childrenIds: childrenIds,
                },
            })
                .then(() => { });
        });
    }
    static unarchive(childrenIds, classId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/unarchive`,
                data: {
                    childrenIds,
                    classId,
                },
            })
                .then(() => { });
        });
    }
    static addToClass(childrenIds, classId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/add-to-class`,
                data: {
                    childrenIds: childrenIds,
                    classId: classId,
                },
            })
                .then(() => { });
        });
    }
    static removeFromClass(childrenIds, classId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/remove-from-class`,
                data: {
                    childrenIds: childrenIds,
                    classId: classId,
                },
            })
                .then(() => { });
        });
    }
    static moveToClass(childrenIds, classId, toClassId, moveYearGroup) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/move-to-class`,
                data: {
                    childrenIds: childrenIds,
                    classId: classId,
                    toClassId: toClassId,
                    moveYearGroup: moveYearGroup,
                },
            })
                .then(() => { });
        });
    }
    static moveToYearGroup(childrenIds, toYearGroupId) {
        return __awaiter(this, void 0, void 0, function* () {
            http()({
                method: 'POST',
                url: '/students/move-to-year-group',
                data: {
                    childrenIds: childrenIds,
                    toYearGroupId: toYearGroupId,
                },
            });
        });
    }
    static addTagToStudents(childrenIds, tagId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/add-tag`,
                data: {
                    childrenIds,
                    tagId,
                },
            })
                .then(() => { });
        });
    }
    static removeTagFromStudents(childrenIds, tagId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'DELETE',
                url: '/students/remove-tag',
                data: {
                    childrenIds,
                    tagId,
                },
            })
                .then(() => { });
        });
    }
    static resetAssessment(childrenIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: '/students/reset-assessment',
                data: {
                    childrenIds,
                },
            })
                .then(() => { });
        });
    }
    static assessmentDelta(childId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'GET',
                url: `/students/${childId}/assessment-delta`,
            })
                .then(({ data }) => data);
        });
    }
    static setAssessmentDelta(childId, delta) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'POST',
                url: `/students/${childId}/assessment-delta`,
                data: {
                    delta
                },
            })
                .then(() => { });
        });
    }
    static completelyDeleteArchived(childrenIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'DELETE',
                url: '/students',
                data: {
                    childrenIds,
                },
            })
                .then(() => { });
        });
    }
}

class NewClassRepository {
    static getClasses(options = { includeArchived: true, includeStudentCount: false, includeAccessCode: false }) {
        return http()({
            method: 'GET',
            url: '/newclass',
            params: options,
        })
            .then((response) => response.data);
    }
    static getStudentsCount(classId) {
        return http()({
            method: 'GET',
            url: `/newclass/studentsCount/${classId}`,
        })
            .then((response) => response.data);
    }
    static create(classData) {
        return http()({
            method: 'POST',
            url: '/newclass',
            data: {
                name: classData.name,
                yearGroupId: classData.yearGroupId,
                color: classData.color,
            },
        })
            .then((response) => response.data);
    }
    static update(id, classData) {
        return http()({
            method: 'PUT',
            url: `/newclass/${id}`,
            data: {
                name: classData.name,
                yearGroupId: classData.yearGroupId,
                color: classData.color,
            },
        })
            .then((response) => response.data);
    }
    static delete(id) {
        return http()({
            method: 'DELETE',
            url: `/newclass/${id}`,
        })
            .then(() => Promise.resolve());
    }
}

class StaffRepository$1 {
    static getStaff() {
        return http()({
            method: 'GET',
            url: '/staff',
        })
            .then((response) => response.data);
    }
    static getFirstAdminEmail(teacherId) {
        return http()({
            method: 'GET',
            url: `/staff/firstAdminEmail/${teacherId}`,
        })
            .then((response) => response.data);
    }
    static addStaff(data) {
        return http()({
            method: 'POST',
            url: '/staff',
            data,
        })
            .then((response) => response.data);
    }
    static updateStaff(teacherId, data) {
        return http()({
            method: 'PUT',
            url: `/staff/${teacherId}`,
            data,
        })
            .then((response) => response.data);
    }
    static deleteStaff(teacherId) {
        return http()({
            method: 'DELETE',
            url: `/staff/${teacherId}`,
        })
            .then((response) => response.data);
    }
}

class TeacherClassRepository {
    static addTeacherClass(data) {
        return http()({
            method: 'POST',
            url: '/teacher-class',
            data,
        })
            .then((response) => response.data);
    }
    static addTeacherClasses(data) {
        return http()({
            method: 'PUT',
            url: '/teacher-class/add-to-classes',
            data,
        })
            .then((response) => response.data);
    }
    static deleteTeacherClass(data) {
        return http()({
            method: 'DELETE',
            url: '/teacher-class',
            data,
        })
            .then((response) => response.data);
    }
    static deleteTeacherClasses(data) {
        return http()({
            method: 'DELETE',
            url: '/teacher-class/delete-from-classes',
            data,
        })
            .then((response) => response.data);
    }
}

class TagsRepository {
    static getTags() {
        return http()({
            method: 'GET',
            url: '/tags',
        })
            .then((response) => response.data);
    }
    static addTag(data) {
        return http()({
            method: 'POST',
            url: '/tags',
            data,
        })
            .then((response) => response.data);
    }
    static updateTag(id, data) {
        return http()({
            method: 'PUT',
            url: `/tags/${id}`,
            data,
        })
            .then((response) => response.data);
    }
    static deleteTag(id) {
        return http()({
            method: 'DELETE',
            url: `/tags/${id}`,
        })
            .then((response) => response.data);
    }
}

class YearGroupRepository {
    static all() {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'GET',
                url: '/year-groups',
            })
                .then(({ data }) => data);
        });
    }
    static allBySchoolId(schoolId) {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'GET',
                url: `/year-groups/${schoolId}`,
            })
                .then(({ data }) => data);
        });
    }
}

class RosterManagementRepository {
    static studentsStatistics() {
        return __awaiter(this, void 0, void 0, function* () {
            return http()({
                method: 'GET',
                url: '/roster-management/statistics/students',
            })
                .then(({ data }) => data);
        });
    }
}

class StaffRepository {
    static addFiles(files) {
        return http()({
            method: 'POST',
            url: '/upload',
            data: files,
        })
            .then(() => { });
    }
}
class ParentRepository {
    static addFiles(locale = null, files) {
        return http()({
            method: 'POST',
            url: '/guardians/upload',
            data: files,
            params: {
                locale: locale instanceof Locale ? locale.id : locale,
            },
        })
            .then(() => { });
    }
}
class SponsorshipRepository {
    static addFiles(files, sponsorshipUrl) {
        const formData = new FormData();
        const file = files[0];
        formData.append('file', file);
        if (sponsorshipUrl) {
            formData.append('sponsorshipUrl', sponsorshipUrl);
        }
        return http()({
            method: 'POST',
            url: '/sponsorship/upload',
            data: formData,
        })
            .then(() => { });
    }
    static getSponsorship(schoolId) {
        return http()({
            method: 'GET',
            url: `/sponsorship/${schoolId !== null && schoolId !== void 0 ? schoolId : ''}`,
        })
            .then(({ data }) => data);
    }
    static deactivate(schoolId) {
        return http()({
            method: 'DELETE',
            url: `/sponsorship/${schoolId !== null && schoolId !== void 0 ? schoolId : ''}`,
        })
            .then(() => { });
    }
}

class AdminRepository {
    static http() {
        const axiosInstance = axios.create({
            baseURL: `https://${process.env.HOSTNAME_API || process.env.REACT_APP_HOSTNAME_API}`,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if ('undefined' === typeof window) {
            return axiosInstance;
        }
        const adminBearer = window.localStorage.getItem(AdminRepository.ADMIN_BEARER_NAME);
        if (null !== adminBearer) {
            axiosInstance.interceptors.request.use(function (config) {
                var _a;
                if (!((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes('/google-login'))) {
                    config.headers.Authorization = `Bearer ${adminBearer}`;
                }
                return config;
            });
        }
        axiosInstance.interceptors.response.use((response) => response, (err) => {
            if (err.response // A response was received
                && 401 === err.response.status // A 401 response was received
                && (Object.keys(err.config.headers).map((key) => key.toLowerCase()).includes('authorization')) // The request was authenticated
            ) {
                window.localStorage.removeItem(AdminRepository.ADMIN_BEARER_NAME);
                window.location.assign(`https://${window.location.host}/admin`);
            }
            return Promise.reject(err);
        });
        return axiosInstance;
    }
    static googleLogin(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.http()({
                method: 'POST',
                url: '/admin/google-login',
                data: data,
            });
            return response.data;
        });
    }
    static getWeeklyLeadersEmails() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.http()({
                method: 'GET',
                url: '/admin/weekly-leaders-emails',
            });
            return response.data;
        });
    }
    static putSchoolWeeklyLeadersEmail(id, data) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.http()({
                method: 'PUT',
                url: `/admin/schools/${id}/weekly-leaders-email`,
                data: data,
            });
        });
    }
    static putSchoolWeeklyLeadersEmailPausedUntil(schools, pausedUntil) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.http()({
                method: 'PUT',
                url: '/admin/weekly-leaders-email/paused-until',
                data: {
                    schools: schools,
                    pausedUntil: pausedUntil.toISODate(),
                },
            });
        });
    }
    static getSchoolTeachers(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.http()({
                method: 'GET',
                url: `/admin/schools/${id}/teachers`,
            });
            return response.data;
        });
    }
    static getMailchimpRenderedTemplate(template, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.http()({
                method: 'POST',
                url: '/admin/mailchimp/rendered-template',
                data: {
                    template: template,
                    params: params,
                },
            });
            return response.data;
        });
    }
    static getSchoolWeeklyLeadersEmailPreviewDynamicData(id, startDate, endDate) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.http()({
                method: 'GET',
                url: `/admin/schools/${id}/weekly-leaders-email/preview-dynamic-data`,
                params: {
                    startDate: startDate.toISO(),
                    endDate: endDate.toISO(),
                },
            });
            return response.data;
        });
    }
}
AdminRepository.ADMIN_BEARER_NAME = 'adminBearer';

const client = {
    http,
};
const model = {
    Assessment,
    AuthHistory,
    AuthStats,
    Avatar,
    Buddy,
    BuddyLeaderboard,
    Challenge,
    ChallengeStats,
    CreditCard,
    Feature,
    Feedback,
    GlobalLeaderboard,
    Identity,
    Item,
    Leaderboard,
    LeaderboardEntry,
    Lesson,
    LessonFeedback,
    LessonLeaderboard,
    LessonLeaderboardEntry,
    LessonResult,
    Locale,
    Message,
    Model,
    OrganizationContact,
    Parent,
    Preference,
    PaymentMethod,
    ReportModel,
    School,
    SchoolLeaderboard,
    SiblingDiscount,
    Sticker,
    Student,
    StudentMathsAgeReport,
    StudentProgressionsReport,
    StudentReportModel,
    StudentTopicsReport,
    StudentUsageReport,
    Subscription,
    SubscriptionPlan,
    Teacher,
    TimeTaken,
    Topic,
    TopicProgress,
    TopicReplay,
    Usage,
    User,
    WeeklySummary,
    GroupReport,
    NewLessonOutcome,
    NewStudentBasic,
    NewStudentTutorData,
    NewStudentWithTags: NewStudentTutorData,
    Group,
    NewTeacher,
    LessonHistory,
    TopicStudentsEntry,
    StudentTopicFocus,
    NewStudentTutorDataMetrics,
    GroupReportMetrics,
    GroupTopicProgress,
    HallOfFame,
    Guardians,
    CsvReports,
    StudentLoginSheets,
    GroupPictureLogin,
};
const repository = {
    GroupReportMetricsRepository,
    SchoolReportRepository,
    GroupTopicReportRepository,
    GroupTopicFocusRepository,
    NewTeacherRepository,
    GroupReportRepository,
    NewStudentTutorDataRepository,
    PdfRepository,
    StudentRepository,
    NewClassRepository,
    StaffRepository: StaffRepository$1,
    TeacherClassRepository,
    TagsRepository,
    YearGroupRepository,
    RosterManagementRepository,
    UploadRepository: StaffRepository,
    ParentUploadRepository: ParentRepository,
    SponsorshipUploadRepository: SponsorshipRepository,
    AdminRepository,
};

export { client, model, repository };
